
import CheckoutButton from "@/components/Cart/CheckoutButton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile';
import { Instagram, LogOut, Mail, Menu, Package, Search, ShoppingCart, User, Coins } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import CartItem from "./CartItem";
import FavoritesDrawer from './FavoritesDrawer';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import HeaderCategories from "./HeaderCategories";
import { useLanguage } from '@/contexts/LanguageContext';

interface HeaderProps {
  hideAuthLinks?: boolean;
}

const Header: React.FC<HeaderProps> = ({ hideAuthLinks = false }) => {
  const { user, logout, isAdmin, userProfile } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { cart, removeFromCart, clearCart, getTotalPoints } = useCart();
  const [isCartOpen, setCartOpen] = useState(false);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  // Get points from AuthContext instead of local state
  const userPoints = userProfile?.points || 0;
  const pointsLastSynced = userProfile?.pointsLastSynced;

  // Check if points balance might be stale (older than 2 minutes)
  const isBalanceStale = pointsLastSynced
    ? (Date.now() - new Date(pointsLastSynced).getTime()) > 120000
    : false;

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
      toast({
        description: t('auth.logoutSuccess'),
      });
    } catch (error) {
      toast({
        title: t('auth.logoutError'),
        description: t('auth.logoutErrorDesc'),
        variant: "destructive",
      });
    }
  };

  const handleCheckoutFromCart = () => {
    setCartOpen(false);
    navigate('/checkout');
  };

  return (
    <>
      {/* Modern Single Header */}
      <header className="bg-brand-darker text-white lg:sticky lg:top-0 z-50 shadow-lg border-b border-gray-800">
        <div className="container mx-auto px-4">
          {/* Top row with social links and auth */}
          <div className="flex justify-between items-center py-2 border-b border-gray-700">
            <div className="flex items-center space-x-6">
              <a href="https://www.instagram.com/dartsbeat_portugal/" className="flex items-center space-x-2 text-sm text-gray-300 hover:text-white transition-colors">
                <Instagram size={14} />
                <span className="hidden sm:inline">Instagram</span>
              </a>
              <a href="mailto:loja@dartsbeatstore.pt" className="hidden md:flex items-center space-x-2 text-sm text-gray-300 hover:text-white transition-colors">
                <Mail size={14} />
                <span>loja@dartsbeatstore.pt</span>
              </a>
            </div>
            <div className="flex items-center space-x-2">
              {!user && !hideAuthLinks && (
                <Link to="/auth" className="text-sm text-gray-300 hover:text-white transition-colors">
                  {t('header.loginWithDartsBeat') || t('header.login')}
                </Link>
              )}
            </div>
          </div>

          {/* Main header row */}
          <div className="flex items-center justify-between py-4">
            <Link to="/" className="flex items-center gap-4">
              <img
                src="/lovable-uploads/2024-10-logo-pt.png"
                alt="DartsBeat Store"
                className="h-12"
              />
              <span className="hidden sm:block text-white text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-wider lg:tracking-widest">
                DARTSBEATSTORE
              </span>
            </Link>

            <div className="hidden md:flex items-center flex-1 max-w-xl mx-8">
              <div className="relative w-full">
                {/* Search can be added here later */}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Points display */}
              <div
                className="hidden md:flex items-center bg-black/30 px-3 py-2 rounded-full gap-2 relative"
                title={pointsLastSynced
                  ? `Última atualização: ${new Date(pointsLastSynced).toLocaleTimeString()}`
                  : 'Saldo de storepoints'
                }
              >
                <Coins className="h-4 w-4 text-brand-red" />
                <span className="text-sm text-gray-300 font-medium">{userPoints}</span>
                {isBalanceStale && (
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full animate-pulse"
                       title="Saldo pode estar desatualizado - atualização automática em breve"
                  />
                )}
              </div>

              {/* Favorites */}
              <FavoritesDrawer />

              {/* Language Switcher */}
              <LanguageSwitcher />

              {/* Cart */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCartOpen(true)}
                className="relative hover:bg-white/10 transition-colors"
              >
                <ShoppingCart className="h-5 w-5 text-gray-200" />
                {cart.length > 0 && (
                  <Badge className="absolute -top-1 -right-1 bg-brand-red rounded-full px-1.5 py-0.5 text-xs font-bold ring-2 ring-brand-darker">
                    {cart.length}
                  </Badge>
                )}
              </Button>

              {/* User Account */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="hover:bg-white/10 transition-colors">
                      <User className="h-5 w-5 text-gray-200" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">
                          {user?.user_metadata?.full_name || t('header.user')}
                        </p>
                        <p className="text-xs leading-none text-muted-foreground">
                          {user?.email}
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                      <DropdownMenuItem asChild>
                        <Link to="/my-account">
                          <User className="mr-2 h-4 w-4" />
                          {t('header.profile')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/my-account?tab=conta">
                          <Coins className="mr-2 h-4 w-4" />
                          {t('header.storePoint')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/my-account?tab=orders">
                          <Package className="mr-2 h-4 w-4" />
                          {t('header.myOrders')}
                        </Link>
                      </DropdownMenuItem>
                    </DropdownMenuGroup>

                    {/* Mostrar link do dashboard apenas para administradores */}
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/dashboard">
                            <Package className="mr-2 h-4 w-4" />
                            {t('header.adminDashboard')}
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}

                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout}>
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>{t('header.logout')}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}

              {/* Mobile menu */}
              <Button variant="ghost" size="icon" className="md:hidden hover:bg-white/10 transition-colors" onClick={() => setMobileMenuOpen(true)}>
                <Menu className="h-5 w-5 text-gray-200" />
              </Button>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="bg-black/50 text-white hidden md:block border-t border-gray-700">
          <div className="container mx-auto px-4">
            <ul className="flex space-x-8 py-3">
              <li>
                <Link to="/" className="hover:text-brand-red font-medium transition-colors">
                  {t('header.home')}
                </Link>
              </li>
              <HeaderCategories />
              <li>
                <Link to="/about-us" className="hover:text-brand-red font-medium">
                  {t('header.about')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-brand-red font-medium">
                  {t('header.contact')}
                </Link>
              </li>
            </ul>
          </div>
        </nav>

        {/* Mobile Menu Sheet */}
        <Sheet open={isMobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent side="left" className="bg-brand-dark text-white flex flex-col h-full min-h-0">
            <SheetHeader className="text-left flex-shrink-0">
              <SheetTitle>{t('header.menu')}</SheetTitle>
            </SheetHeader>
            <div className="flex-1 min-h-0 mt-4 -mr-4 pr-4 overflow-y-auto">
              <nav className="pb-6">
                <ul className="space-y-3">
                  <li>
                    <Link to="/" className="block py-2 hover:text-brand-red" onClick={() => setMobileMenuOpen(false)}>
                      {t('header.home')}
                    </Link>
                  </li>
                  <li>
                    <HeaderCategories isMobile={true} onItemClick={() => setMobileMenuOpen(false)} />
                  </li>
                  <li>
                    <Link to="/about-us" className="block py-2 hover:text-brand-red" onClick={() => setMobileMenuOpen(false)}>
                      {t('header.about')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/contact" className="block py-2 hover:text-brand-red" onClick={() => setMobileMenuOpen(false)}>
                      {t('header.contact')}
                    </Link>
                  </li>
                  
                  {/* User Account Section for Mobile */}
                {user && (
                  <>
                    <li className="pt-4 border-t border-gray-700">
                      <div className="text-xs text-gray-400 mb-2 font-medium">{t('header.myAccount').toUpperCase()}</div>
                      <div className="text-sm text-gray-300 mb-3">
                        <div>{user?.user_metadata?.full_name || t('header.user')}</div>
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <Coins className="h-3 w-3 text-brand-red" />
                          {userPoints}
                        </div>
                      </div>
                    </li>
                    <li>
                      <Link to="/my-account?tab=dados" className="flex items-center space-x-2 py-2 hover:text-brand-red" onClick={() => setMobileMenuOpen(false)}>
                        <User size={16} />
                        <span>{t('header.profile')}</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/my-account?tab=conta" className="flex items-center space-x-2 py-2 hover:text-brand-red" onClick={() => setMobileMenuOpen(false)}>
                        <Coins size={16} />
                        <span>{t('header.storePoint')}</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/my-account?tab=orders" className="flex items-center space-x-2 py-2 hover:text-brand-red" onClick={() => setMobileMenuOpen(false)}>
                        <Package size={16} />
                        <span>{t('header.myOrders')}</span>
                      </Link>
                    </li>

                    {/* Mostrar link do dashboard apenas para administradores no menu mobile */}
                    {isAdmin && (
                      <li>
                        <Link to="/dashboard" className="flex items-center space-x-2 py-2 hover:text-brand-red" onClick={() => setMobileMenuOpen(false)}>
                          <Package size={16} />
                          <span>{t('header.adminDashboard')}</span>
                        </Link>
                      </li>
                    )}

                    <li>
                      <button
                        onClick={() => {
                          handleLogout();
                          setMobileMenuOpen(false);
                        }}
                        className="flex items-center space-x-2 py-2 hover:text-brand-red w-full text-left"
                      >
                        <LogOut size={16} />
                        <span>{t('header.logout')}</span>
                      </button>
                    </li>
                  </>
                )}

                  {/* Contact info */}
                  <li className="pt-4">
                    <a href="mailto:loja@dartsbeatstore.pt" className="flex items-center space-x-2 text-sm hover:text-brand-red py-2">
                      <Mail size={16} />
                      <span>loja@dartsbeatstore.pt</span>
                    </a>
                  </li>

                  {/* Auth link for non-logged users */}
                  {!user && !hideAuthLinks && (
                    <li className="pt-4 border-t border-gray-700">
                      <Link to="/auth" className="block py-2 hover:text-brand-red" onClick={() => setMobileMenuOpen(false)}>
                        {t('header.loginWithDartsBeat') || t('header.login')}
                      </Link>
                    </li>
                  )}
                </ul>
              </nav>
            </div>
          </SheetContent>
        </Sheet>

        {/* Cart Sheet */}
        <Sheet open={isCartOpen} onOpenChange={setCartOpen}>
          <SheetContent className="bg-brand-dark text-white sm:max-w-lg flex flex-col">
            <SheetHeader className="text-left flex-shrink-0">
              <SheetTitle>{t('cart.title')}</SheetTitle>
              <SheetDescription>
                {cart.length === 0 ? t('cart.empty') : t('cart.checkItems')}
              </SheetDescription>
            </SheetHeader>
            
            {/* Scrollable cart items area */}
            <div className="flex-1 overflow-hidden">
              <ScrollArea className="h-full py-4">
                {cart.map((item) => (
                  <CartItem key={item.productId} item={item} removeFromCart={removeFromCart} />
                ))}
              </ScrollArea>
            </div>
            
            {/* Fixed bottom section with total and buttons */}
            {cart.length > 0 && (
              <div className="flex-shrink-0 border-t border-gray-700 pt-4 space-y-4">
                <div className="text-right text-lg font-semibold">
                  Total: {getTotalPoints()} storepoints
                </div>
                <CheckoutButton 
                  totalPoints={getTotalPoints()} 
                  onClick={handleCheckoutFromCart}
                />
                <Button onClick={() => {
                  clearCart();
                  setCartOpen(false);
                  toast({
                    description: t('cart.cleared'),
                  });
                }} variant="outline" className="w-full">
                  {t('cart.clear')}
                </Button>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </header>
    </>
  );
};

export default Header;
